import {
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { FormikErrors, useFormikContext } from 'formik';
import { isEqual } from 'lodash';

import { fetchCalculatedPrice, InsuranceDetails } from 'Api/insurance';
import { PriceDto } from 'Api/insurance/types/insurance.dto.types';
import { ProductName } from 'Constants/products.constants';
import { hasFutureChanges } from 'Forms/ChangeForm/products/commonBuilding/alerts/IssueDateMessage/issueDateMessage.helpers';
import React from 'react';
import { CarCoverage } from 'Types/coverages/coverages.types';
import { useDebounce } from 'use-debounce';
import { isCarProduct, isTravelInsurance } from 'Utils/insuranceTypes';
import { isCoverageInForm } from 'Utils/products.utils';
import {
  ChangeFormPriceRequest,
  ChangeFormValues,
  PriceFields,
} from '../../changeForm.types';
import {
  isNewCarSuperCoverage,
  isOldCarSuperCoverage,
} from '../../products/car/changeCarForm.helpers';
import { priceCalculationQueryKeyPrefix } from './priceFetcher.constants';
import { PriceFetchState } from './priceFetcher.types';

/**
 * NOTE: This function is temporary and should be removed when logic for switching between super old and super new is implemented in lisa.
 */
export const useIsValuesEqualWithoutRentalType = (
  insurance: InsuranceDetails,
): boolean => {
  const { initialValues, values } = useFormikContext<PriceFields>();
  if (
    'rentalType' in values &&
    isOldCarSuperCoverage(insurance.coverage as CarCoverage)
  ) {
    const initialValuesWithRentalTypeNull = {
      ...initialValues,
      rentalType: null,
    };
    const valuesWithRentalTypeNull = { ...values, rentalType: null };
    return isEqual(initialValuesWithRentalTypeNull, valuesWithRentalTypeNull);
  }
  return false;
};

export const hasFutureChangesAndIsSameAsToday = (
  todaysInitialValues: ChangeFormValues,
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
  insurance: InsuranceDetails,
): boolean => {
  return (
    hasFutureChanges(
      todaysInitialValues,
      initialValues,
      insurance.insuranceProduct,
    ) && isEqualCheck(values, todaysInitialValues, insurance.insuranceProduct)
  );
};

const isEqualCheck = (
  values: ChangeFormValues,
  todaysInitialValues: ChangeFormValues,
  insuranceProduct: ProductName,
) => {
  if (
    isCarProduct(insuranceProduct) &&
    isCoverageInForm(values) &&
    isCoverageInForm(todaysInitialValues)
  ) {
    if (
      isOldCarSuperCoverage(todaysInitialValues.coverage as CarCoverage) &&
      isNewCarSuperCoverage(values.coverage as CarCoverage)
    ) {
      return isEqual(
        { ...values, coverage: CarCoverage.FULL_SUPER_V2, rentalType: null },
        {
          ...todaysInitialValues,
          coverage: CarCoverage.FULL_SUPER_V2,
          rentalType: null,
        },
      );
    }
  }

  return isEqual(values, todaysInitialValues);
};

export const useDisableChangeButton = (
  todaysInitialValues: ChangeFormValues,
  insurance: InsuranceDetails,
): boolean => {
  const { initialValues, values, errors } =
    useFormikContext<ChangeFormValues>();
  const isFormWithErrors = hasFormErrors(errors);
  const isFutureChangesAndIsSameAsToday = hasFutureChangesAndIsSameAsToday(
    todaysInitialValues,
    initialValues,
    values,
    insurance,
  );

  return isFormWithErrors || isFutureChangesAndIsSameAsToday;
};

const useGetAlteredContext = (
  insurance: InsuranceDetails,
  initialValues: ChangeFormValues,
  values: ChangeFormValues,
  todaysInitialValues: ChangeFormValues,
  errors: FormikErrors<ChangeFormValues>,
) => {
  if (isTravelInsurance(insurance.insuranceProduct)) {
    return {
      initialValuesProcessed: {
        ...initialValues,
        coverageConfirmation: undefined,
      },
      valuesProcessed: { ...values, coverageConfirmation: undefined },
      todaysInitialValuesProcessed: {
        ...todaysInitialValues,
        coverageConfirmation: undefined,
      },
      errorsProcessed: {
        ...removeErrorFromErrors(errors, 'coverageConfirmation'),
      },
    };
  }

  return {
    initialValuesProcessed: initialValues,
    valuesProcessed: values,
    todaysInitialValuesProcessed: todaysInitialValues,
    errorsProcessed: errors,
  };
};

const removeErrorFromErrors = (
  errors: FormikErrors<ChangeFormValues>,
  errorKey: string,
): FormikErrors<ChangeFormValues> => {
  if (typeof errorKey === 'string' && errorKey in errors) {
    // Create a new errors object to avoid mutating the original
    const newErrors = { ...errors };
    delete newErrors[errorKey as keyof typeof newErrors];
    return newErrors;
  }

  // Return original if no change was made
  return errors;
};

const usePriceFetchState = (
  insurance: InsuranceDetails,
  todaysInitialValues: ChangeFormValues,
): PriceFetchState => {
  const {
    initialValues,
    values: formValues,
    errors,
  } = useFormikContext<ChangeFormValues>();

  const [values] = useDebounce(formValues, 500);

  const {
    initialValuesProcessed,
    valuesProcessed,
    todaysInitialValuesProcessed,
    errorsProcessed,
  } = useGetAlteredContext(
    insurance,
    initialValues,
    values,
    todaysInitialValues,
    errors,
  );

  const { priceCalculationQueryKey, priceCalculationCachedData } =
    useGetPriceCalculationQueryContext(insurance.policyNumber, valuesProcessed);

  const isInitialState = isEqual(initialValuesProcessed, valuesProcessed);
  // NOTE: When doing rebrand for CAR for isEqualWithoutRentalType  refactor this out car specific logic
  const isEqualWithoutRentalType = useIsValuesEqualWithoutRentalType(insurance);
  const isFormWithErrors = hasFormErrors(errorsProcessed);
  const isFutureChangesAndIsSameAsToday = hasFutureChangesAndIsSameAsToday(
    todaysInitialValuesProcessed,
    initialValuesProcessed,
    valuesProcessed,
    insurance,
  );

  const shouldNotFetchPrice =
    isInitialState ||
    isEqualWithoutRentalType ||
    isFormWithErrors ||
    isFutureChangesAndIsSameAsToday;

  return {
    shouldFetchNewPrice: !shouldNotFetchPrice,
    priceCalculationQueryKey,
    priceCalculationCachedData,
  };
};

export const hasFormErrors = (
  errors: FormikErrors<ChangeFormValues>,
): boolean => {
  return Object.keys(errors).length !== 0;
};

export const useGetNewPrice = (
  insurance: InsuranceDetails,
  mappedValues: ChangeFormPriceRequest,
  todaysInitialValues: ChangeFormValues,
): UseQueryResult<PriceDto | null, Error> => {
  const {
    shouldFetchNewPrice,
    priceCalculationQueryKey,
    priceCalculationCachedData,
  } = usePriceFetchState(insurance, todaysInitialValues);

  return useQuery({
    queryKey: priceCalculationQueryKey,
    queryFn: () => {
      if (priceCalculationCachedData || !shouldFetchNewPrice) {
        return null;
      }
      return fetchCalculatedPrice(insurance, mappedValues);
    },
    enabled: () => {
      return !priceCalculationCachedData && shouldFetchNewPrice;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
};

type PriceContextState = UseQueryResult<PriceDto | null, Error> | null;

export const PriceContext = React.createContext<PriceContextState>(null);

export const usePriceContext = (): PriceContextState => {
  return React.useContext(PriceContext);
};

export const useGetPriceCalculationQueryContext = (
  policyNumber: string,
  values: ChangeFormValues,
) => {
  const queryClient = useQueryClient();
  const stableValuesKey = JSON.stringify(
    values,
    Object.keys(values).sort((a, b) => a.localeCompare(b)),
  );
  const priceCalculationQueryKey = [
    priceCalculationQueryKeyPrefix,
    policyNumber,
    stableValuesKey,
  ];
  const cachedData = queryClient.getQueryData<PriceDto | null>(
    priceCalculationQueryKey,
  );
  return {
    priceCalculationQueryKey,
    priceCalculationCachedData: cachedData,
  };
};

export const useClearPriceCalculationQueryCache = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.removeQueries({
      predicate: (query) => {
        return query.queryKey.includes(priceCalculationQueryKeyPrefix);
      },
    });
};
